import type {
  Annotation,
  AnnotationCoordinate,
  AnnotationCreationTool,
  AnnotationLayer,
  AnnotationLayerId,
  AnnotationTextStyle,
  AreaAnnotationStyle,
} from './model';
import { annotationPresetStyle } from './typography';

const defaultAreaStyle: AreaAnnotationStyle = {
  fillColour: '#2f6fed',
  fillOpacity: 0.16,
  outlineColour: '#234a83',
  outlineWidth: 2,
  outlineDash: 'solid',
};

export const LEGACY_ANNOTATION_TEXT_STYLE: AnnotationTextStyle = {
  fontFamily: 'inter',
  fontSize: 16,
  colour: '#172033',
  alignment: 'left',
  backgroundColour: '#ffffff',
  backgroundOpacity: 0.92,
  borderColour: '#c4cad3',
  borderWidth: 1,
  padding: 5,
  boxWidth: 180,
  cornerRadius: 0,
};

export type AnnotationIdFactory = (prefix: 'ann' | 'anl') => string;
const createId: AnnotationIdFactory = (prefix) => `${prefix}_${crypto.randomUUID()}`;

export function createAnnotationLayer(
  existingLayers: readonly AnnotationLayer[],
  factory: AnnotationIdFactory = createId,
): AnnotationLayer {
  let number = 1;
  const names = new Set(existingLayers.map((layer) => layer.name));
  while (names.has(`Annotations ${number}`)) number += 1;
  return {
    id: factory('anl'),
    name: `Annotations ${number}`,
    visible: true,
    annotations: [],
  };
}

function nextName(layer: AnnotationLayer, tool: AnnotationCreationTool): string {
  const label =
    tool === 'focus-area'
      ? 'Focus area'
      : tool === 'line'
        ? 'Arrow'
        : `${tool[0]!.toUpperCase()}${tool.slice(1)}`;
  if (tool === 'focus-area' && !layer.annotations.some((item) => item.type === tool)) return label;
  let number = 1;
  const names = new Set(layer.annotations.map((item) => item.name));
  while (names.has(`${label} ${number}`)) number += 1;
  return `${label} ${number}`;
}

function closeRing(coordinates: readonly AnnotationCoordinate[]): readonly AnnotationCoordinate[] {
  const first = coordinates[0]!;
  const last = coordinates.at(-1)!;
  return first[0] === last[0] && first[1] === last[1] ? coordinates : [...coordinates, first];
}

export function rectangleRing(
  first: AnnotationCoordinate,
  opposite: AnnotationCoordinate,
): readonly AnnotationCoordinate[] {
  return [first, [opposite[0], first[1]], opposite, [first[0], opposite[1]], first];
}

export function createAnnotation(
  layer: AnnotationLayer,
  tool: AnnotationCreationTool,
  coordinates: readonly AnnotationCoordinate[],
  factory: AnnotationIdFactory = createId,
): Annotation {
  const base = {
    id: factory('ann'),
    name: nextName(layer, tool),
    visible: true,
  };
  switch (tool) {
    case 'marker':
      return {
        ...base,
        type: tool,
        position: coordinates[0]!,
        style: {
          shape: 'circle',
          fillColour: '#e34b4b',
          outlineColour: '#ffffff',
          size: 14,
          opacity: 1,
        },
      };
    case 'line':
      return {
        ...base,
        type: tool,
        coordinates,
        style: { colour: '#26364a', width: 3, opacity: 1, dash: 'solid', endStyle: 'arrow' },
      };
    case 'polygon':
      return { ...base, type: tool, coordinates: closeRing(coordinates), style: defaultAreaStyle };
    case 'rectangle':
      return {
        ...base,
        type: tool,
        coordinates: rectangleRing(coordinates[0]!, coordinates[1]!),
        rotationDegrees: 0,
        style: { ...defaultAreaStyle, cornerRadius: 0 },
      };
    case 'ellipse':
      return {
        ...base,
        type: tool,
        bounds: [coordinates[0]!, coordinates[1]!],
        circleLocked: false,
        rotationDegrees: 0,
        style: defaultAreaStyle,
      };
    case 'text':
      return {
        ...base,
        type: tool,
        position: coordinates[0]!,
        text: 'Text',
        style: annotationPresetStyle('halo'),
      };
    case 'callout':
      return {
        ...base,
        type: tool,
        keepLeaderVerticalOnScreen: true,
        target: coordinates[0]!,
        textPosition: coordinates[1]!,
        text: 'Callout',
        style: {
          ...annotationPresetStyle('framed'),
          leaderColour: '#475569',
          leaderWidth: 3,
          leaderStyle: 'tapered',
          leaderOpacity: 0.8,
          leaderDash: 'solid',
          targetStyle: 'filled-circle',
        },
      };
    case 'focus-area':
      return {
        ...base,
        type: tool,
        coordinates: closeRing(coordinates),
        style: {
          fadeColour: '#ffffff',
          fadeOpacity: 0.62,
          boundaryVisible: true,
          boundaryColour: '#374151',
          boundaryWidth: 2,
          boundaryDash: 'dashed',
          fadeDirection: 'outside',
          scope: 'basemap-and-data',
        },
      };
  }
}

export function annotationLayerById(
  layers: readonly AnnotationLayer[],
  id: AnnotationLayerId | null,
): AnnotationLayer | undefined {
  return id ? layers.find((layer) => layer.id === id) : undefined;
}
